import { useState, useCallback } from 'react';

const STORAGE_KEY = 'palcare_perPage';
const VALID_VALUES = [25, 50, 100, 200, 300];
const DEFAULT_PER_PAGE = 25;

/**
 * The per-page choices every pager must offer — exactly the values this hook
 * accepts. Render the <select> from this list. Several compact pagers used to
 * offer [10, 25, 50, 100]: choosing 10 silently snapped to 25, and a stored 200 or
 * 300 (picked on a full-size pager) matched no option, so the select read "10"
 * while 200+ rows were showing.
 */
export const PER_PAGE_OPTIONS = VALID_VALUES;

/**
 * usePersistedPerPage — shared hook for persisting rows-per-page to localStorage.
 *
 * All V2 pagination tables share a single stored value so changing the
 * preference on one page applies everywhere and persists across sessions.
 *
 * Usage:
 *   const [perPage, setPerPage] = usePersistedPerPage();
 *
 * @returns {[number, (value: number) => void]}
 */
export default function usePersistedPerPage() {
  const [perPage, setPerPageState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored !== null) {
        const parsed = parseInt(stored, 10);
        if (VALID_VALUES.includes(parsed)) return parsed;
      }
    } catch {
      // localStorage unavailable — fall back to default
    }
    return DEFAULT_PER_PAGE;
  });

  const setPerPage = useCallback((value) => {
    const num = typeof value === 'string' ? parseInt(value, 10) : value;
    const safe = VALID_VALUES.includes(num) ? num : DEFAULT_PER_PAGE;
    setPerPageState(safe);
    try {
      localStorage.setItem(STORAGE_KEY, safe.toString());
    } catch {
      // localStorage unavailable — value still persists in state for this session
    }
  }, []);

  return [perPage, setPerPage];
}
